import { omit } from 'remeda';
import z from 'zod';

import { ParagraphCodec, PlateParagraph, UnityParagraph } from './Paragraph';
import { createElement } from '../../creators/createElement';
import {
    CURRENT_VERSION,
    ElementId,
    emptyChildren,
    EmptyChildren,
    UnityElementId,
    Version,
} from '../commons';
import { ELEMENT_TYPES, MIME_TYPES } from '../constants';

export const UnityInstagram = z.object({
    type: z.literal(ELEMENT_TYPES.INSTAGRAM),
    mimetype: z.literal(MIME_TYPES.INSTAGRAM),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    originalSrc: z.string().nullish(),
    src: z.string().nullable(),
    contentAttributes: z.object({
        includeCaption: z.boolean().nullish(),
        caption: z
            .object({
                richTextFormat: z.array(UnityParagraph),
            })
            .nullish(),
        credit: z.string().nullish(),
    }),
});
export type UnityInstagram = z.infer<typeof UnityInstagram>;

export const PlateInstagram = z.object({
    type: z.literal(ELEMENT_TYPES.INSTAGRAM),
    id: ElementId,
    data: z.object({
        mimetype: z.literal(MIME_TYPES.INSTAGRAM),
        templateElement: z.boolean().optional(),
        originalSrc: z.string().nullish(),
        src: z.string().nullable(),
        embed: z.object({
            caption: z.array(PlateParagraph),
            credit: z.string(),
        }),
        includeCaption: z.boolean().nullish(),
    }),
    children: EmptyChildren,
});
export type InstagramElement = z.infer<typeof PlateInstagram>;

export const InstagramCodec = z.codec(UnityInstagram, PlateInstagram, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        data: {
            mimetype: value.mimetype,
            originalSrc: value.originalSrc,
            src: value.src,
            embed: {
                caption: value.contentAttributes.caption
                    ? value.contentAttributes.caption.richTextFormat.map((paragraph) =>
                          ParagraphCodec.decode(paragraph),
                      )
                    : [createElement(ELEMENT_TYPES.PARAGRAPH, { children: emptyChildren })],
                credit: value.contentAttributes.credit ?? '',
            },
            includeCaption: value.contentAttributes.includeCaption,
            ...(value.templateElement && { templateElement: value.templateElement }),
        },
        children: emptyChildren,
    }),
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        version: CURRENT_VERSION,
        ...omit(value.data, ['embed']),
        contentAttributes: {
            caption: {
                richTextFormat: value.data.embed.caption.map((paragraph) =>
                    ParagraphCodec.encode(paragraph),
                ),
            },
            credit: value.data.embed.credit,
            includeCaption: value.data.includeCaption,
        },
    }),
});
